import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useUi } from '../context/UiContext';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function BrandDashboard() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { showToast, confirmAction } = useUi();
  const [brand, setBrand] = useState(null);
  const [leads, setLeads] = useState(null);

  const [month, setMonth] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const [uploads, setUploads] = useState(null);

  const load = () => {
    api.getBrand(brandId).then(setBrand).catch(() => navigate('/lead-quality-score'));
    api.listLeads(brandId).then(setLeads).catch(() => setLeads([]));
  };

  const loadUploads = () => {
    api.listMonthlyUploads(brandId).then(setUploads).catch(() => setUploads([]));
  };

  useEffect(() => { load(); loadUploads(); }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteBrand = async () => {
    const ok = await confirmAction(
      'Delete Brand',
      `Are you sure you want to delete "${brand?.brand_name}"? All its leads and monthly results will be deleted, and its Google Drive folder (Client Data / ${brand?.brand_name}) will be moved to the Drive trash.`
    );
    if (!ok) return;
    try {
      await api.deleteBrand(brandId);
    } catch (err) {
      showToast(err.detail || 'Could not delete the brand.', 'error');
      return;
    }
    showToast('Brand deleted.', 'info');
    navigate('/lead-quality-score');
  };

  const deleteLead = async (lead) => {
    const ok = await confirmAction(
      'Delete Lead',
      `Are you sure you want to delete the lead for "${lead.full_name}"? This cannot be undone.`
    );
    if (!ok) return;
    await api.deleteLead(lead.id);
    showToast('Lead deleted.', 'info');
    load();
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    setUploadError('');
    if (!picked) {
      setFile(null);
      return;
    }
    const lower = picked.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.csv')) {
      setUploadError('Please upload a valid .xlsx or .csv file.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFile(picked);
  };

  const handleResult = async () => {
    if (!month) {
      setUploadError('Please select a month before viewing results.');
      return;
    }
    if (!file) {
      setUploadError('Please upload a .xlsx or .csv file before viewing results.');
      return;
    }

    setSubmitting(true);
    setUploadError('');
    try {
      const upload = await api.createMonthlyUpload(brandId, month, file);
      if (upload.google_drive_status === 'saved') {
        showToast(`Lead Quality Score generated successfully. Raw Leads Sheet saved to Google Drive.`, 'success');
      } else {
        showToast(
          `Lead Quality Score generated successfully, but the Raw Leads Sheet could not be saved to Google Drive. Please try again.`,
          'error'
        );
      }
      loadUploads();
    } catch (err) {
      setUploadError(err.detail || 'Failed to process the file.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!brand || leads === null || uploads === null) return null;

  return (
    <div className="container page-content">
      <div className="page-header">
        <div className="brand-header-top">
          <div className="brand-header-left">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/lead-quality-score')}>
              <BackIcon /> Back to Home
            </button>
          </div>
          <h2 className="brand-header-title">{brand.brand_name}</h2>
          <div className="page-header-actions brand-header-actions">
            <button className="btn btn-danger btn-sm" onClick={deleteBrand}>Delete Brand</button>
          </div>
        </div>
      </div>

      <div className="section-header">
        <div>
          <h3 className="section-title">Lead Quality Analysis</h3>
          <p className="section-subtitle">All leads for this brand with automatically calculated quality scores.</p>
        </div>
      </div>

      <div className="card mb-24 monthly-score-card">
        <div className="card-header">
          <h4 style={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-ink-faint)' }}>
            Monthly Lead Quality Score
          </h4>
        </div>
        <div className="card-body">
          <div className="flex items-start" style={{ justifyContent: 'center', gap: 28 }}>
            <select
              className="form-control monthly-action-control"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setUploadError(''); }}
            >
              <option value="">Month</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <button className="btn btn-secondary monthly-action-control" onClick={() => fileInputRef.current?.click()}>
                Upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {file && (
                <span
                  className="upload-filename"
                  title={file.name}
                >
                  {file.name}
                </span>
              )}
            </div>

            <button className="btn btn-primary monthly-action-control" onClick={handleResult} disabled={submitting}>
              {submitting ? 'Processing…' : 'Result'}
            </button>
          </div>

          {uploadError && (
            <p style={{ color: 'var(--red)', fontSize: '0.82rem', marginTop: 12 }}>{uploadError}</p>
          )}
          <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-faint)', marginTop: 12, marginBottom: 0, whiteSpace: 'nowrap' }}>
            Select a month, upload a .xlsx or .csv file of lead criteria, then click Result to automatically calculate scores.
          </p>
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="mb-24">
          <div className="monthly-cards-grid">
            {uploads.map((u) => (
              <div
                key={u.id}
                className="card card-hover"
                style={{ padding: 20, cursor: 'pointer' }}
                onClick={() => navigate(`/lead-quality-score/brands/${brandId}/monthly/${u.id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', textTransform: 'uppercase' }}>{u.month}</h4>
                  <span className={`badge badge-${percentClass(u.overall_percentage)}`}>{u.overall_percentage}%</span>
                </div>
                <div style={{ marginTop: 10, fontSize: '0.82rem', color: 'var(--color-ink-soft)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span>Total Leads: <strong>{u.total_leads}</strong></span>
                </div>
                <DriveStatus status={u.google_drive_status} url={u.google_drive_file_url} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="leads-section">
        {leads.length > 0 && (
          <div className="leads-table-wrap">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Location</th>
                  <th>Total Score</th>
                  <th>Marketing</th>
                  <th>Sales</th>
                  <th>Classification</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const cls = lead.score.classification;
                  return (
                    <tr key={lead.id} onClick={() => navigate(`/lead-quality-score/brands/${brandId}/leads/${lead.id}`)}>
                      <td>
                        <div className="lead-name-cell">
                          <div className="name">{lead.full_name}</div>
                          <div className="service">{lead.service_required || '—'}</div>
                        </div>
                      </td>
                      <td>{lead.location || '—'}</td>
                      <td>
                        <div className="score-pill">
                          <span className={`score-dot ${cls.cls}`} />
                          <span style={{ fontWeight: 700, color: 'var(--color-ink)' }}>{lead.score.total_score}</span>
                          <span style={{ color: 'var(--color-ink-faint)', fontSize: '0.75rem' }}>/ 100</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)' }}>Marketing</div>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{lead.score.marketing_score}/80</div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)' }}>Sales</div>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{lead.score.sales_score}/20</div>
                        </div>
                      </td>
                      <td><span className={`badge badge-${cls.cls}`}>{cls.label}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Link
                            className="btn btn-ghost btn-sm"
                            to={`/lead-quality-score/brands/${brandId}/leads/${lead.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            View
                          </Link>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={(e) => { e.stopPropagation(); deleteLead(lead); }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DriveStatus({ status, url }) {
  if (status === 'saved') {
    return (
      <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--green)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        ✓ Saved to Google Drive
        {url && (
          <>
            {' · '}
            <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'inherit', textDecoration: 'underline' }}>
              Open in Drive
            </a>
          </>
        )}
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--red)', whiteSpace: 'nowrap' }}>
        ⚠ Not saved to Google Drive
      </div>
    );
  }
  return null;
}

function percentClass(pct) {
  if (pct >= 80) return 'high';
  if (pct >= 60) return 'qualified';
  if (pct >= 40) return 'low';
  return 'invalid';
}

function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
